/**
 * @module engine/export/pdf/color
 * @summary Turn a CSS colour string into the 0-255 RGB + 0-1 alpha a PDF needs.
 * @description
 *   The draw functions were written for a browser canvas, so they speak CSS:
 *   `'#fff'`, `'#2b579a'`, `'rgba(0,0,0,0.05)'`. A PDF content stream speaks
 *   `r g b rg` with alpha living somewhere else entirely (an ExtGState), so the
 *   two have to be split apart before anything can be drawn.
 *
 *   Only the forms the drawing code actually uses are parsed — hex (3/4/6/8
 *   digits), `rgb()`, `rgba()`, and the handful of names a user could type into
 *   a colour field. Anything unrecognised falls back to black rather than
 *   throwing: a symbol drawn in the wrong colour is a blemish, a symbol that
 *   aborts the export is a lost drawing.
 */

/** A colour split into what the content stream needs and what the ExtGState needs. */
export interface Rgba {
  /** 0-255. */
  r: number;
  g: number;
  b: number;
  /** 0-1. */
  a: number;
}

const BLACK: Rgba = { r: 0, g: 0, b: 0, a: 1 };

/** The few names that can reach us from a hand-typed colour field. */
const NAMED: Record<string, string> = {
  black: '#000000',
  white: '#ffffff',
  red: '#ff0000',
  green: '#008000',
  blue: '#0000ff',
  yellow: '#ffff00',
  orange: '#ffa500',
  grey: '#808080',
  gray: '#808080',
  transparent: 'rgba(0,0,0,0)',
};

const cache = new Map<string, Rgba>();

/** Parse a CSS colour. Unknown input becomes opaque black. */
export function parseColor(css: string): Rgba {
  if (!css) return BLACK;
  const hit = cache.get(css);
  if (hit) return hit;
  const out = parse(css.trim().toLowerCase());
  cache.set(css, out);
  return out;
}

function parse(s: string): Rgba {
  const named = NAMED[s];
  if (named) s = named;

  if (s.startsWith('#')) {
    const h = s.slice(1);
    // #rgb / #rgba — each digit is a nibble that doubles up.
    if (h.length === 3 || h.length === 4) {
      const n = (i: number) => parseInt(h[i] + h[i], 16);
      return {
        r: n(0),
        g: n(1),
        b: n(2),
        a: h.length === 4 ? n(3) / 255 : 1,
      };
    }
    if (h.length === 6 || h.length === 8) {
      const n = (i: number) => parseInt(h.slice(i, i + 2), 16);
      return {
        r: n(0),
        g: n(2),
        b: n(4),
        a: h.length === 8 ? n(6) / 255 : 1,
      };
    }
    return BLACK;
  }

  // rgb(r,g,b) / rgba(r,g,b,a), comma- or space-separated.
  const m = /^rgba?\(([^)]*)\)$/.exec(s);
  if (m) {
    const parts = m[1].split(/[\s,/]+/).filter(Boolean).map(Number);
    if (parts.length >= 3 && parts.every((v) => Number.isFinite(v))) {
      return {
        r: clamp255(parts[0]),
        g: clamp255(parts[1]),
        b: clamp255(parts[2]),
        a: parts.length > 3 ? Math.max(0, Math.min(1, parts[3])) : 1,
      };
    }
  }

  return BLACK;
}

function clamp255(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)));
}
